import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";

const routes = [
  {
    path: "/",
    name: "dashboard",
    component: () => import("@/views/Dashboard.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/Login.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/bank-account",
    name: "bank-account",
    component: () => import("@/views/BankAccount.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/referidos",
    name: "referidos",
    component: () => import("@/views/Referidos.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/transactions",
    name: "transactions",
    component: () => import("@/views/Transactions.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/ruleta",
    name: "ruleta",
    component: () => import("@/views/Ruleta.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/terms",
    name: "terms",
    component: () => import("@/views/Terms.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/privacy",
    name: "privacy",
    component: () => import("@/views/Privacy.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Async navigation guard
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // Check for token in URL parameters first, then localStorage
  const urlParams = new URLSearchParams(window.location.search);
  const tokenInUrl = urlParams.get("token");
  const tokenInStorage = localStorage.getItem("authToken");

  // If there's a token in URL, use it and override localStorage
  if (tokenInUrl) {
    console.log(
      "Router: Token found in URL parameters, overriding localStorage"
    );

    // If user is already authenticated with a different token, logout first
    if (
      store.getters.isAuthenticated &&
      tokenInStorage &&
      tokenInUrl !== tokenInStorage
    ) {
      console.log(
        "Router: Different token detected, clearing old authentication"
      );
      store.dispatch("logout");
    }

    localStorage.setItem("authToken", tokenInUrl);
  }

  // Wait for auth initialization to complete if there's a token
  if (!store.getters.isAuthenticated && (tokenInUrl || tokenInStorage)) {
    console.log("Router: Waiting for auth initialization...");
    try {
      const authResult = await store.dispatch("initializeAuth");
      if (authResult === false) {
        console.log(
          "Router: Auth initialization returned false - token was invalid"
        );
        // The sessionInvalid flag should already be set by initializeAuth
      }
    } catch (error) {
      console.error("Router: Auth initialization failed:", error);
      // Clear invalid token
      localStorage.removeItem("authToken");
      // Set session invalid flag to show appropriate message
      sessionStorage.setItem("sessionInvalid", "true");
      console.log(
        "Router: Set sessionInvalid flag due to auth initialization failure"
      );
    }
  }

  // Check if token is expired even if authenticated
  if (store.getters.isAuthenticated && store.getters.isTokenExpired) {
    console.log("Token is expired, redirecting to login");
    store.dispatch("logout");
    // Set session invalid flag to show appropriate message
    sessionStorage.setItem("sessionInvalid", "true");
    next("/login");
    return;
  }

  const isAuthenticated = store.getters.isAuthenticated;

  if (requiresAuth && !isAuthenticated) {
    // Check if there was a token that failed validation
    const hadToken = tokenInUrl || tokenInStorage;
    if (hadToken) {
      // Set session invalid flag if there was a token that failed
      sessionStorage.setItem("sessionInvalid", "true");
      console.log(
        "Router: Set sessionInvalid flag due to failed token validation"
      );
    }
    next("/login");
  } else if (to.name === "login" && isAuthenticated) {
    next("/");
  } else {
    next();
  }
});

export default router;
